import React, { useState } from 'react';
import { Plus, Loader2, RefreshCw, X } from 'lucide-react';

export default function GenericManageQueriesView({
  queries,
  onAddQuery,
  onDeleteQuery,
  onRefresh,
  isComplex = false,
  contentType,
}) {
  const [title, setTitle] = useState('');
  const [queryString, setQueryString] = useState('');
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [error, setError] = useState(null);

  const handleAdd = async () => {
    let finalQueryString = queryString;
    if (isComplex) {
      if (!title.trim() || !category.trim() || !keyword.trim()) return;
      finalQueryString = JSON.stringify({ category, keyword, fromDate, toDate });
    } else if (!title.trim() || !queryString.trim()) {
      return;
    }

    setIsAdding(true);
    setError(null);
    try {
      await onAddQuery({
        description: title,
        query_text: finalQueryString,
        content_type: contentType,
      });
      setTitle('');
      setQueryString('');
      setCategory('');
      setKeyword('');
      setFromDate('');
      setToDate('');
    } catch (err) {
      setError(err.message || 'Failed to add query.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleting(id);
    setError(null);
    try {
      await onDeleteQuery(id);
    } catch (err) {
      setError(err.message);
      setIsDeleting(null);
    }
  };

  return (
    <div>
      {error && (
        <div className="bg-red-900/50 text-red-300 p-3 rounded-lg mb-4 border border-red-700">
          {error}
        </div>
      )}
      <div className="bg-gray-900/50 p-6 rounded-lg mb-8">
        <h3 className="text-xl font-semibold text-gray-300 mb-4">Create New Query</h3>
        <div className="space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              isComplex ? 'Query Description / Title' : 'Query Title'
            }
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={isAdding}
          />
          {isComplex ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Category"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                disabled={isAdding}
              />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Keyword Name"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                disabled={isAdding}
              />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                disabled={isAdding}
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                disabled={isAdding}
              />
            </div>
          ) : (
            <textarea
              value={queryString}
              onChange={(e) => setQueryString(e.target.value)}
              placeholder="Query Text..."
              className="w-full h-24 bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
              disabled={isAdding}
            />
          )}
          <button
            onClick={handleAdd}
            disabled={
              isAdding ||
              !title.trim() ||
              (isComplex
                ? !category.trim() || !keyword.trim()
                : !queryString.trim())
            }
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center"
          >
            {isAdding ? (
              <Loader2 className="animate-spin mr-2" />
            ) : (
              <Plus size={18} className="mr-1" />
            )}{' '}
            Add Query
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-300">Existing Queries</h3>
        <button
          onClick={onRefresh}
          disabled={isAdding || !!isDeleting}
          className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded-lg flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh List
        </button>
      </div>
      <div className="bg-gray-900/50 p-4 rounded-lg overflow-y-auto max-h-[400px]">
        {queries.length > 0 ? (
          <ul className="space-y-3">
            {queries.map((q) => {
              let displayDetails = q.queryString;
              if (isComplex) {
                try {
                  const parsed = JSON.parse(q.queryString);
                  displayDetails = `${parsed.category} | ${parsed.keyword} (${parsed.fromDate || '?'} - ${parsed.toDate || '?'})`;
                } catch (e) {
                  // ignore parse error, fallback to raw string
                }
              }
              return (
                <li
                  key={q.id}
                  className="bg-gray-700/50 p-3 rounded-lg flex justify-between items-center"
                >
                  <div>
                    <p className="font-semibold text-white">{q.title}</p>
                    <p className="text-sm text-gray-400 font-mono">{displayDetails}</p>
                    {q.schedule_type && (
                      <span className="text-xs bg-cyan-800 text-cyan-200 px-2 py-0.5 rounded-full ml-2">
                        {q.schedule_type}{' '}
                        {q.frequency_interval ? `(${q.frequency_interval})` : ''}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(q.id)}
                    disabled={!!isDeleting}
                    className="text-red-500 hover:text-red-400 disabled:text-gray-500 disabled:cursor-not-allowed p-1"
                  >
                    {isDeleting === q.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-gray-500 text-center">No queries created yet.</p>
        )}
      </div>
    </div>
  );
}

